import { useAppSelector } from "../../app/hooks";
import { selectHotel } from "../../features/hotel/hotelSlice";
import { findRoomCategory } from "../../helpers/roomCategory";
import { getPriceByRate } from "../../helpers/reservation";
import { useTranslation } from "react-i18next";
import FormatPrice from "../Helpers/FormatPrice";
import FormatPricing from "../Helpers/FormatPricing";
import {
  removeBookingRoom,
  selectBooking,
} from "../../features/booking/bookingSlice";
import { useEffect, useState } from "react";
import type { IHotelRate } from "../../features/hotel/hotelInterface";
import { formatCurrency } from "../../helpers/currency";
import XMark from "../Svg/XMark";
import { useDispatch } from "react-redux";
import { formatDateLong } from "../../helpers/date";
import { findPublicRate } from "../../helpers/rate";

export default function ReservationCard(props: {
  reservation: any;
  showRate?: boolean;
  discount?: boolean;
}) {
  const { reservation, showRate = true, discount = false } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const [roomCategoryName, setRoomCategoryName] = useState<string>("");
  const [rate, setRate] = useState<IHotelRate>();
  const [pricing, setPricing] = useState<any>(null);
  useEffect(() => {
    const roomCategory = findRoomCategory(reservation.RoomCategoryId);
    setRoomCategoryName(
      roomCategory?.Name[hotel.Culture.LanguageCode] ||
        roomCategory?.Name["en-US"] ||
        "",
    );
    setPricing(getPriceByRate(reservation));
    setRate(findPublicRate(reservation.RateId));
  }, [reservation, hotel.RoomCategoryAvailabilities, hotel.Rates]);

  const removeReservation = (reservation: any) => {
    const reservationIndex = booking.Rooms.findIndex((r) => {
      return (
        r.RoomCategoryId === reservation.RoomCategoryId &&
        r.RateId === reservation.RateId &&
        r.AdultCount === reservation.AdultCount &&
        r.ChildCount === reservation.ChildCount &&
        JSON.stringify([...(r.ProductIds || [])].sort()) ===
          JSON.stringify([...(reservation.ProductIds || [])].sort())
      );
    });

    if (reservationIndex === -1) return;

    dispatch(removeBookingRoom(reservationIndex));
  };

  if (pricing) {
    const currencyCode = hotel.Culture.CurrencyCode ?? "EUR";
    const hasReferenceDiscount =
      pricing.ReferencePrice &&
      pricing.ReferencePrice.AveragePerNight[currencyCode] >
        pricing.Price.AveragePerNight[currencyCode];
    const hasMaxPriceDiscount =
      pricing.MaxPrice.AveragePerNight[currencyCode] >
      pricing.Price.AveragePerNight[currencyCode];
    const hasDiscount = hasReferenceDiscount || hasMaxPriceDiscount;
    const hasVoucherDiscount =
      !discount && hasReferenceDiscount && rate?.Id !== reservation.RateId;

    return (
      <div className="flex gap-4 w-full">
        <div className="flex flex-col flex-frow gap-2 w-full">
          <div className="flex justify-between text-fluid-base">
            <div>
              {reservation.count} {roomCategoryName}
              {reservation.count === 1 ? "" : "s"}
              {` (${reservation.AdultCount} ${t("scenes.traveller", {
                count: reservation.AdultCount,
              })})`}
            </div>
            <span className="text-right flex flex-wrap items-baseline justify-end gap-x-2">
              <FormatPricing
                pricing={pricing}
                quantity={reservation.count * booking.NightCount}
              />
              {((discount && hasDiscount) || hasVoucherDiscount) && (
                <span className="text-gray-500 line-through text-fluid-sm">
                  <FormatPricing
                    pricing={pricing}
                    referencePrice={hasReferenceDiscount}
                    maxPrice={!hasReferenceDiscount && hasMaxPriceDiscount}
                    quantity={reservation.count * booking.NightCount}
                  />
                </span>
              )}
            </span>
          </div>

          <div className="text-fluid-base flex justify-between text-gray-700">
            <span className={`text-fluid-sm`}>
              {formatDateLong(reservation.StartUtc, false)} 15:00
              {" - "}
              {formatDateLong(reservation.EndUtc, false)} 12:00
            </span>
          </div>

          {discount && hasDiscount && (
              <div className="text-fluid-base text-custom-500 flex justify-between">
                <span>{t("scenes.summary.discount")}</span>
                <span>
                  <FormatPricing
                    pricing={pricing}
                    discountPrice={true}
                    quantity={reservation.count * booking.NightCount}
                  />
                </span>
              </div>
            )}

          <div>
            {showRate && (
              <div className="text-fluid-xs">
                <span className="">{t("scenes.summary.rate")} </span>
                <span className="text-gray-700">
                  <FormatPrice
                    price={
                      pricing.Price.AveragePerNight[hotel.Culture.CurrencyCode]
                    }
                  />{" "}
                  {t("scenes.summary.perNight")}
                </span>
                {hasVoucherDiscount && (
                  <span className="text-gray-500 line-through ml-2">
                    <FormatPricing pricing={pricing} referencePrice={true} />{" "}
                    {t("scenes.summary.perNight")}
                  </span>
                )}
              </div>
            )}

            <div className="text-fluid-xs">
              {showRate && (
                <>
                  <span>{t("scenes.summary.rate")} </span>
                  <span className="text-gray-700">
                    {rate?.Name[hotel.Culture.LanguageCode] ||
                      rate?.Name["en-US"]}{" "}
                  </span>
                </>
              )}
            </div>

            {reservation.ProductIds.map((productId: string) => {
              const product = hotel.Products.find((p) => p.Id === productId);
              if (product) {
                return (
                  <div
                    key={productId}
                    className="flex justify-between text-gray-700 text-fluid-xs"
                  >
                    <div>
                      <span>
                        {product.Name[hotel.Culture.LanguageCode] ||
                          product.Name["en-US"]}{" "}
                      </span>
                      <span className="">
                        <FormatPrice
                          price={product.Prices[hotel.Culture.CurrencyCode]}
                        />{" "}
                        {product.Charging === "PerPersonNight" &&
                          t("scenes.extras.perPersonPerNight")}
                      </span>
                    </div>
                    <span>
                      {formatCurrency(
                        product.Prices[hotel.Culture.CurrencyCode] *
                          reservation.AdultCount *
                          booking.NightCount,
                      )}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>

        <div
          className="p-2 bg-custom-200 rounded-md self-start cursor-pointer"
          onClick={() => removeReservation(reservation)}
        >
          <XMark className="size-4 text-black" />
        </div>
      </div>
    );
  } else {
    return null;
  }
}
